/**
 * Driver alerts tick — pure helper extracted from coreSlice.nextDay
 * (02/06/2026 night refactor).
 *
 * Detects two distinct driver-level alert conditions :
 *
 *   1. BURNOUT EARLY-WARNING (Nadia Cheikh perk) : a driver with active
 *      status whose daysToCritical projection sits in the 5–9 day window.
 *   2. LOW-SATISFACTION RENEGOTIATION : edge-trigger when satisfaction
 *      crosses below 30 (vs 30+ on the previous tick). Not gated behind
 *      a support role.
 *
 * Each rule respects a per-driver cooldown to avoid spamming the inbox.
 *
 * Pure : no `set()`, no `get()`. Returns the lists of driver ids that
 * trigger each rule ; the orchestrator is responsible for actually
 * pushing the templated messages and applying the cooldowns.
 */

import { analyzeDriverCompatibility } from '../../utils/compatibility';

/**
 * @param {Object} state
 * @param {Array}  driversAfterLease - post-fatigue, post-lease driver array
 * @param {Object} opts
 * @param {Function} opts.hasSupportUnlock - get().hasSupportUnlock or () => false
 * @returns {{ burnoutAlerts: string[], renegotiationAlerts: string[] }}
 */
export function runDriverAlertsTick(state, driversAfterLease, opts = {}) {
  const burnoutAlerts = [];
  const renegotiationAlerts = [];
  const totalDays = state.totalDays || 0;
  const hasNadia = !!(state.hiredSupport || {})['role-nadia-cheikh'];
  const hasSupportUnlock = opts.hasSupportUnlock || (() => false);

  // 4.5 BURNOUT EARLY-WARNING
  if (typeof hasSupportUnlock === 'function' && hasNadia) {
    driversAfterLease.forEach(d => {
      if (d.status !== 'active') return;
      const cooldown = d.burnoutAlertCooldown || 0;
      if (cooldown > totalDays) return;
      const analysis = analyzeDriverCompatibility(d, state.vehicles, state.activeContracts);
      if (analysis.daysToCritical === null
          || analysis.daysToCritical === Infinity
          || analysis.daysToCritical < 5
          || analysis.daysToCritical > 9) return;
      burnoutAlerts.push(d.id);
    });
  }

  // 4.6 LOW-SATISFACTION RENEGOTIATION
  // 11/02/2026 (fix user prod Amina récurrent v2) — En plus du
  // `renegotiationCooldown` propre, on gate maintenant AUSSI sur
  // `lastRaiseRequestDay` (90 j) et `lastRaiseDecisionDay` (60 j refus /
  // 180 j accord). Sans ce gate croisé, un chauffeur qui vient d'essuyer un
  // refus d'augmentation via drv-raise-01 perdait -12 satisfaction, tombait
  // sous 30, et déclenchait DANS LA FOULÉE une renégociation « urgente »
  // pour la même demande de salaire → user prod voyait 2 demandes en
  // quelques jours.
  driversAfterLease.forEach(d => {
    const prevDriver = state.drivers.find(x => x.id === d.id);
    if (!prevDriver) return;
    const cooldown = d.renegotiationCooldown || 0;
    if (cooldown > totalDays) return;
    // Gate croisé avec les demandes d'augmentation.
    const lastReq = d.lastRaiseRequestDay || 0;
    if (totalDays - lastReq < 90) return;
    const lastDec = d.lastRaiseDecisionDay || 0;
    if (lastDec > 0) {
      const outcome = d.lastRaiseDecisionOutcome;
      // 25/07/2026 (choix user 3a) — aligné sur les nouveaux cooldowns :
      // 360 j de tranquillité après hausse accordée, 90 j après refus.
      const raiseCooldown = outcome === 'accepted' ? 360 : 90;
      if (totalDays - lastDec < raiseCooldown) return;
    }
    // Edge-trigger : crossing below 30.
    if (prevDriver.satisfaction >= 30 && d.satisfaction < 30) {
      renegotiationAlerts.push(d.id);
    }
  });

  return { burnoutAlerts, renegotiationAlerts };
}
